import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, AlertTriangle, ShieldCheck } from "lucide-react";

const F = (key, label, wide) => ({ key, label, wide });
const SECTIONS = [
  { title: "Company Identity", fields: [
    F("legal_name", "Legal Company Name"), F("display_name", "Display / Brand Name"),
    F("website", "Website"), F("logo", "Company Logo URL"),
  ]},
  { title: "Registration & Tax IDs", fields: [
    F("cin", "CIN"), F("llpin", "LLPIN"), F("pan", "PAN"), F("gstin", "GSTIN"), F("msme", "Udyam / MSME Number"),
  ]},
  { title: "Official Contact", fields: [
    F("business_email", "Official Business Email"), F("phone", "Corporate Phone"),
  ]},
  { title: "Addresses", fields: [
    F("registered_address", "Registered Address", true), F("corporate_address", "Corporate Address", true),
    F("city", "City"), F("state", "State"), F("country", "Country"), F("pin", "PIN Code"),
  ]},
  { title: "Authorized Signatory",
    note: "Mandatory for agreement generation. These fields are never auto-filled — an authorized Super Admin must configure the real signatory.",
    fields: [
      F("signatory_name", "Authorized Signatory Name"), F("signatory_designation", "Designation"),
      F("signatory_email", "Official Signatory Email"), F("signatory_phone", "Signatory Phone"),
    ]},
];
const DEFAULT_FIELDS = [
  F("governing_law", "Default Governing Law"), F("jurisdiction", "Default Jurisdiction"),
  F("notice_period", "Default Notice Period"), F("duration", "Default Agreement Duration"),
  F("renewal_terms", "Default Renewal Terms"), F("confidentiality_period", "Default Confidentiality Period"),
];

function FieldGrid({ fields, values, onChange, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
      {fields.map((f) => (
        <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
          <Label className="text-xs">{f.label}</Label>
          <Input value={values[f.key] ?? ""} onChange={(e) => onChange(f.key, e.target.value)}
            className="mt-1" data-testid={`${prefix}-${f.key}`} />
        </div>
      ))}
    </div>
  );
}

export default function CompanyProfile() {
  const [data, setData] = React.useState(null);
  const [profile, setProfile] = React.useState({});
  const [defaults, setDefaults] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    api.get("/admin/company-profile").then((r) => {
      setData(r.data);
      const { defaults: d, updated_at, updated_by, ...rest } = r.data.profile || {};
      setProfile(rest); setDefaults(d || {});
    }).catch((e) => { setData(false); toast.error(apiError(e.response?.data?.detail)); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/company-profile", { profile, defaults });
      toast.success(`Company Profile saved — ${r.data.changed.length} field(s) updated and audited`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(apiError(d));
    } finally { setBusy(false); }
  };

  if (data === null) return <SkeletonRows rows={8} />;
  if (data === false) return <p className="text-sm text-slate-400">Unable to load Company Profile.</p>;

  const missing = [...(data.missing_base || []), ...(data.missing_commercial || [])];

  return (
    <div className="pb-8" data-testid="company-profile-page">
      <PageHeader title="Company Profile" breadcrumb="VS Command Center · System Settings · Company Profile"
        description="The single authoritative Party A source for every generated NDA, SLA and agreement. Changes are audited per field; existing agreements keep their original snapshot.">
        <Button onClick={save} disabled={busy} className="bg-vs-royal text-white" data-testid="company-profile-save">
          <Save className="h-4 w-4 mr-1" />{busy ? "Saving…" : "Save Profile"}</Button>
      </PageHeader>

      {missing.length > 0 && (
        <div className="mb-5 rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2" data-testid="company-profile-missing">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">Agreement generation is blocked until these mandatory Party A fields are configured: <strong>{missing.join(", ")}</strong>.</p>
        </div>
      )}
      {missing.length === 0 && (
        <div className="mb-5 rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex gap-2" data-testid="company-profile-complete">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-800">All mandatory Party A fields are configured. Agreements can be generated.</p>
        </div>
      )}

      <div className="space-y-5">
        {SECTIONS.map((s) => (
          <Card key={s.title} title={s.title} subtitle={s.note}>
            <FieldGrid fields={s.fields} values={profile} prefix="cpf"
              onChange={(k, v) => setProfile((p) => ({ ...p, [k]: v }))} />
          </Card>
        ))}
        <Card title="Agreement Defaults" subtitle="Configurable defaults threaded into every new agreement — never hardcoded in templates.">
          <FieldGrid fields={DEFAULT_FIELDS} values={defaults} prefix="cpd"
            onChange={(k, v) => setDefaults((p) => ({ ...p, [k]: v }))} />
        </Card>
      </div>

      {(data.profile?.updated_at || data.profile?.updated_by) && (
        <p className="text-xs text-slate-400 mt-4" data-testid="company-profile-meta">
          Last updated by {data.profile.updated_by || "system"} · {data.profile.updated_at ? new Date(data.profile.updated_at).toLocaleString() : ""}
        </p>
      )}
    </div>
  );
}
